import { useState, useEffect } from 'react';
import { Drawer } from 'antd';
import styled from 'styled-components';
import { Button, Input } from '@nextui-org/react';
import { IoRefreshCircle } from 'react-icons/io5';
import {
  AbstractCategory,
  AbstractCategoryWithSubcategories,
} from '../../../data/types/categories';
import { useDispatch, useSelector } from 'react-redux';
import ShowImage from '../../../components/show_image';
import { Colors, Fonts } from '../../../config/theme';
import {
  actionGetCategoriesMinimalList,
  actionSetLastFetchedCategoriesMinimalList,
} from '../../../store/categories/categories_actions';
import { buildCategoryTree } from '../../../utils/functions/categories_functions';

type CategoryModalProps = {
  isModalVisible: boolean;
  hideModal: () => void;
  onConfirm: (cat: AbstractCategoryWithSubcategories) => void;
  onClear: () => void;
};

export default function CategoryModal({
  isModalVisible,
  hideModal,
  onConfirm,
  onClear,
}: CategoryModalProps) {
  // Get the category tree from Redux store
  const dispatch = useDispatch();
  const minimalCategoriesTree = useSelector(
    (state: any) => state.CategoriesBase.minimalCategoriesTree,
  );

  const [categoriesData, setCategoriesData] = useState<
    AbstractCategoryWithSubcategories[][]
  >([minimalCategoriesTree]); // Store the tree as an array of category arrays
  const [selectedCat, setSelectedCat] = useState<
    AbstractCategoryWithSubcategories | undefined
  >();

  const [filteredCategories, setFilteredCategories] = useState(categoriesData);
  const [searchQuery, setSearchQuery] = useState('');

  const addSubCategories = (
    category: AbstractCategoryWithSubcategories,
    index: number,
  ) => {
    // Immutably update the categories data by removing deeper levels and adding subcategories
    const updatedData = [...categoriesData.slice(0, index + 1)];
    if (category.sub_categories && category.sub_categories.length > 0) {
      updatedData.push(category.sub_categories);
    }
    setCategoriesData(updatedData); // Set updated categories data
  };

  const categorySelection = (
    cat: AbstractCategoryWithSubcategories,
    key: number,
  ) => {
    // Add subcategories for the selected category and update the selected category
    addSubCategories(cat, key);
    setSelectedCat(cat);
  };

  const CategoryCol = (
    categories: AbstractCategoryWithSubcategories[],
    key: number,
  ) => {
    return (
      <div className="category-col" key={key}>
        {categories &&
          categories.length > 0 &&
          categories.map((category) => (
            <div
              className={`item ${selectedCat?.id === category.id ? 'active' : ''}`}
              title="click to show"
              key={category.id}
              onClick={() => categorySelection(category, key)}
            >
              {category.name_en + ' | ' + category.name_ar}
              <ShowImage
                image={category.category_image}
                style={{
                  maxWidth: '75px',
                  maxHeight: '75px',
                }}
              />
            </div>
          ))}
      </div>
    );
  };

  const categoriesView = () => {
    return (
      <>
        {filteredCategories.map((categoryLevel: any, index: number) => {
          return CategoryCol(categoryLevel, index);
        })}
      </>
    );
  };

  const searchCategoriesRecursive = (
    query: string,
    categories: AbstractCategoryWithSubcategories[],
  ): AbstractCategoryWithSubcategories[] => {
    return categories
      .map((category) => {
        const matches = category.name_en
          .toLowerCase()
          .includes(query.toLowerCase());

        const filteredSubcategories = category.sub_categories
          ? searchCategoriesRecursive(query, category.sub_categories)
          : [];

        if (matches || filteredSubcategories.length > 0) {
          return { ...category, sub_categories: filteredSubcategories };
        }

        return null;
      })
      .filter(Boolean) as AbstractCategoryWithSubcategories[];
  };

  const searchCategories = (query: string) => {
    if (!query) return categoriesData;

    return categoriesData.map((categoryArray) =>
      searchCategoriesRecursive(query, categoryArray),
    );
  };

  useEffect(() => {
    setFilteredCategories(searchCategories(searchQuery));
  }, [searchQuery, categoriesData]);

  return (
    <Drawer
      open={isModalVisible}
      maskClosable={true}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 20px',
            marginBottom: '20px',
          }}
        >
          <div>
            <Button
              style={{
                width: '150px',
                color: 'white',
                marginRight: '10px',
              }}
              onClick={() => {
                hideModal();
                setSelectedCat(undefined);
              }}
            >
              Exit without saving
            </Button>
            <Button
              color="warning"
              style={{
                width: '150px',
                color: 'white',
              }}
              onClick={() => {
                onClear();
                setSelectedCat(undefined);
              }}
            >
              Clear
            </Button>
          </div>
          <Button
            color="success"
            style={{
              width: '150px',
            }}
            onClick={() => {
              if (selectedCat) onConfirm(selectedCat);
            }}
            disabled={!selectedCat}
          >
            Confirm
          </Button>
        </div>
      }
      width={'80%'}
      closable={false}
      title={
        <>
          <h4>Select the item’s category</h4>
          <p>
            Select the category in which you are going to sell the product.
            Didn’t find an appropriate category?{' '}
            <span>Contact us and we'll add it!</span>
          </p>
          <p>
            <span style={{ color: Colors.primary }}>Note:</span> If you think
            you're seeing outdated categories, click the refresh icon.
          </p>
          <Button
            isIconOnly={true}
            onPress={() => {
              const payload = {
                success: (newMinimalCategoriesList: AbstractCategory[]) => {
                  const tree = buildCategoryTree(newMinimalCategoriesList);
                  setCategoriesData([tree]);
                  setSelectedCat(undefined);
                },
              };
              dispatch(actionSetLastFetchedCategoriesMinimalList(0));
              dispatch(actionGetCategoriesMinimalList(payload));
            }}
            color="success"
            variant="flat"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
            }}
          >
            <IoRefreshCircle size={25} />
          </Button>
        </>
      }
    >
      <Wrapper>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <div className="mt-2 mb-2 flex justify-between w-full gap-7">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100px',
                maxHeight: '100px',
                width: '55%',
              }}
            >
              <p>
                Currently selected category:{' '}
                {selectedCat && (
                  <span style={{ color: Colors.primary }}>
                    {selectedCat.name_en + ' | ' + selectedCat.name_ar}
                  </span>
                )}
              </p>
              <p>{selectedCat?.description}</p>
            </div>
            <div style={{ width: '35%' }}>
              <Input
                type="text"
                label="Search Categories"
                list="categories"
                variant="bordered"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div
            className="categories-main"
            style={{
              flex: 6,
            }}
          >
            {categoriesView()}
          </div>
        </div>
      </Wrapper>
    </Drawer>
  );
}

const Wrapper = styled.div`
  .categories-main {
    background: #fafafa;
    border: 1px solid rgba(0, 39, 51, 0.1);
    border-radius: 5px;
    margin-bottom: 20px;
    width: 100%;
    overflow-x: scroll;
    display: flex;
    justify-content: start;
    overflow-x: auto;

    .category-col {
      height: 600px; /* Set a fixed height for the column */
      min-width: calc(100% / 5);
      border-right: 1px solid rgba(0, 39, 51, 0.1);
      overflow-y: auto;
      overflow-x: hidden;

      &:last-of-type {
        border-right: none;
      }

      ::-webkit-scrollbar {
        width: 8px;
      }

      ::-webkit-scrollbar-track {
        background: #0027330d;
      }

      ::-webkit-scrollbar-thumb {
        background: #00273326;
        border-radius: 5px;
      }

      .item {
        height: 75px;
        padding: 10px 20px;
        font-family: ${Fonts.secondary};
        font-weight: 400;
        font-size: 15px;
        line-height: 104%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #002733;
        border-bottom: 1px solid rgba(0, 39, 51, 0.1);
        cursor: pointer;

        &:last-of-type {
          border-bottom: none;
        }

        &:hover,
        &.active {
          background: #daecea;
        }

        svg {
          path {
            fill: #0027331a;
          }
        }
      }
    }
  }
`;
